export type TLanguage = {
  name: string
  label: string
}

export const allLanguages: TLanguage[] = [
  { name: 'ar-EG', label: '🇪🇬 Arabic (Egypt)' },
  { name: 'ar-SA', label: '🇸🇦 Arabic (Saudi Arabia)' },
  { name: 'bg-BG', label: '🇧🇬 Bulgarian' },
  { name: 'ca-ES', label: '🇪🇸 Catalan (Spain)' },
  { name: 'zh-HK', label: '🇭🇰 Chinese (Cantonese, Traditional)' },
  { name: 'zh-CN', label: '🇨🇳 Chinese (Mandarin, Simplified)' },
  { name: 'zh-TW', label: '🇹🇼 Chinese (Taiwanese Mandarin)' },
  { name: 'hr-HR', label: '🇭🇷 Croatian' },
  { name: 'cs-CZ', label: '🇨🇿 Czech' },
  { name: 'da-DK', label: '🇩🇰 Danish' },
  { name: 'nl-NL', label: '🇳🇱 Dutch' },
  { name: 'en-AU', label: '🇦🇺 English (Australia)' },
  { name: 'en-CA', label: '🇨🇦 English (Canada)' },
  { name: 'en-IN', label: '🇮🇳 English (India)' },
  { name: 'en-IE', label: '🇮🇪 English (Ireland)' },
  { name: 'en-GB', label: '🇬🇧 English (UK)' },
  { name: 'en-US', label: '🇺🇸 English (US)' },
  { name: 'et-EE', label: '🇪🇪 Estonian (Estonia)' },
  { name: 'fi-FI', label: '🇫🇮 Finnish' },
  { name: 'fil-PH', label: '🇵🇭 Filipino (Philippines)' },
  { name: 'fr-CA', label: '🇨🇦 French (Canada)' },
  { name: 'fr-FR', label: '🇫🇷 French (France)' },
  { name: 'fr-CH', label: '🇨🇭 French (Switzerland)' },
  { name: 'de-AT', label: '🇦🇹 German (Austria)' },
  { name: 'de-DE', label: '🇩🇪 German (Germany)' },
  { name: 'de-CH', label: '🇨🇭 German (Switzerland)' },
  { name: 'el-GR', label: '🇬🇷 Greek' },
  { name: 'he-IL', label: '🇮🇱 Hebrew (Israel)' },
  { name: 'hi-IN', label: '🇮🇳 Hindi (India)' },
  { name: 'hu-HU', label: '🇭🇺 Hungarian' },
  { name: 'ga-IE', label: '🇮🇪 Irish (Ireland)' },
  { name: 'id-ID', label: '🇮🇩 Indonesian' },
  { name: 'it-IT', label: '🇮🇹 Italian' },
  { name: 'ja-JP', label: '🇯🇵 Japanese' },
  { name: 'ko-KR', label: '🇰🇷 Korean' },
  { name: 'lv-LV', label: '🇱🇻 Latvian (Latvia)' },
  { name: 'lt-LT', label: '🇱🇹 Lithuanian (Lithuania)' },
  { name: 'mt-MT', label: '🇲🇹 Maltese (Malta)' },
  { name: 'ms-MY', label: '🇲🇾 Malay' },
  { name: 'nb-NO', label: '🇳🇴 Norwegian' },
  { name: 'pl-PL', label: '🇵🇱 Polish' },
  { name: 'pt-BR', label: '🇧🇷 Portuguese (Brazil)' },
  { name: 'pt-PT', label: '🇵🇹 Portuguese (Portugal)' },
  { name: 'ro-RO', label: '🇷🇴 Romanian' },
  { name: 'ru-RU', label: '🇷🇺 Russian' },
  { name: 'sk-SK', label: '🇸🇰 Slovak' },
  { name: 'sl-SI', label: '🇸🇮 Slovenian' },
  { name: 'es-MX', label: '🇲🇽 Spanish (Mexico)' },
  { name: 'es-ES', label: '🇪🇸 Spanish (Spain)' },
  { name: 'sv-SE', label: '🇸🇪 Swedish' },
  { name: 'ta-IN', label: '🇮🇳 Tamil (India)' },
  { name: 'te-IN', label: '🇮🇳 Telugu (India)' },
  { name: 'th-TH', label: '🇹🇭 Thai' },
  { name: 'tr-TR', label: '🇹🇷 Turkish (Turkey)' },
  { name: 'vi-VN', label: '🇻🇳 Vietnamese' },
]

export function findLanguageByCode(code: string | null | undefined): TLanguage | undefined {
  if (!code) {
    return undefined
  }
  return allLanguages.find((l) => l.name === code)
}

export function findLanguageByCodeOrPrefix(code: string | null | undefined): TLanguage | undefined {
  if (!code) {
    return undefined
  }

  const normalized = code.trim().toLowerCase()
  if (!normalized) {
    return undefined
  }

  const exact = allLanguages.find((l) => l.name.toLowerCase() === normalized)
  if (exact) {
    return exact
  }

  const prefix = normalized.split('-')[0]
  if (!prefix) {
    return undefined
  }

  return allLanguages.find((l) => l.name.toLowerCase().startsWith(`${prefix}-`))
}

export function toLanguageWhitelist(codes: string[]): string[] {
  const unique = new Set<string>()

  for (const code of codes) {
    const normalized = code.trim().toLowerCase()
    if (!normalized) {
      continue
    }

    const prefix = normalized.split('-')[0]
    if (!prefix) {
      continue
    }

    unique.add(prefix)
  }

  return [...unique]
}

export function isEnglishCode(code: string | null | undefined): boolean {
  return !!code && code.toLowerCase().startsWith('en-')
}

const languageCountry: Record<string, string> = {
  en: 'GB',
  zh: 'CN',
  ja: 'JP',
  ko: 'KR',
  fr: 'FR',
  de: 'DE',
  es: 'ES',
  pt: 'PT',
  it: 'IT',
  pl: 'PL',
  ru: 'RU',
  nl: 'NL',
  ar: 'SA',
}

export function languageFlag(languageCodes: string[]): string {
  const code = languageCodes[0]

  if (!code) return ''

  const region = code.includes('-') ? code.split('-')[1] : languageCountry[code.toLowerCase()]

  if (!region || region.length !== 2) return ''

  return String.fromCodePoint(...[...region.toUpperCase()].map((char) => 0x1f1e6 + (char.codePointAt(0) ?? 65) - 65))
}

// Returns a clean human-readable language name suitable for prompt injection,
// stripping the leading flag emoji and the regional suffix
// (e.g., "🇫🇷 French (France)" → "French"). Falls back to the raw code
// when the code is unknown.
export function getPromptLanguageName(code: string | null | undefined): string {
  const lang = findLanguageByCode(code)
  if (!lang) {
    return code ?? ''
  }
  const firstLetter = lang.label.search(/[A-Za-z]/)
  const labelNoFlag = (firstLetter > 0 ? lang.label.slice(firstLetter) : lang.label).trim()
  const parenIdx = labelNoFlag.indexOf(' (')
  return parenIdx === -1 ? labelNoFlag : labelNoFlag.slice(0, parenIdx)
}
